import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeart,
  faPlus,
  faPenToSquare,
  faTrash,
  faPaperPlane,
  faClockRotateLeft,
  faLightbulb,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from 'antd';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  CartesianGrid
} from 'recharts';

import { useStore } from '../../hooks/storeContext';
import { usePageHeader } from '../../layouts/AdminLayout';
import PulseService from './PulseService';
import Markdown from './Markdown';
import {
  PulseRoot,
  UtilityBar,
  UtilityButton,
  HistoryPanel,
  HistoryItem,
  PromptMenu,
  MessageScroll,
  MessageContainer,
  Bubble,
  ToolCallList,
  ToolCallCard,
  DataTable,
  Typing,
  Composer,
  ComposerInner,
  Suggestions,
  EmptyState
} from './styled';

const SUGGESTIONS = [
  'How many high-risk patients do we have right now?',
  'How many patients have we seen in each county?',
  'How many of our patients are uninsured?',
  'Show me the patient demographics breakdown',
  'Give me the LDL-C stratification by sex',
  'A1C breakdown by ADA category for uninsured patients',
  'What is the ethnicity breakdown of our patients?',
  'Compare screening volume over the last 30 vs previous 30 days',
  'How many events have we held and what types?',
  'What are patient stress levels like?',
  'Average blood pressure and cholesterol across all screenings'
];

// Friendly display names for the data tools shown in "View the data" cards.
const TOOL_LABELS = {
  get_overview: 'System overview',
  get_patient_demographics: 'Patient demographics',
  get_risk_distribution: 'Risk distribution',
  get_screening_trends: 'Screening trends',
  get_event_stats: 'Event statistics',
  get_volunteer_stats: 'Volunteer statistics',
  get_queue_stats: 'Intake queue',
  get_health_outcomes_summary: 'Health outcomes',
  count_patients_by_filter: 'Patient count',
  get_geographic_distribution: 'Geographic distribution',
  get_social_factors: 'Insurance & stress',
  get_filtered_health_metrics: 'Filtered health metrics',
  compare_periods: 'Period comparison',
  get_booking_funnel: 'Booking funnel',
  get_health_improvement: 'Health improvement',
  get_outreach_stats: 'Outreach engagement',
  get_survey_stats: 'Survey activity',
  get_report: 'Custom report'
};

const prettifyKey = (key) => String(key)
  .replace(/_/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/^\w/, (c) => c.toUpperCase());

const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

// Build a link resolver for one assistant message. The model sometimes
// rewrites the tool's relative reportsUrl (dropping params) or prefixes an
// invented domain like https://example.com, which sends staff off-site. So:
//  - absolute URLs pointing at an in-app /admin/ route become relative paths;
//  - every Custom Reports link is replaced by the EXACT reportsUrl the tools
//    returned (exact match first, otherwise the next unused one in order).
function makeHrefResolver (toolCalls) {
  const urls = (toolCalls || [])
    .map((tc) => tc?.result?.reportsUrl)
    .filter((u) => typeof u === 'string' && u.startsWith('/'));
  const used = new Set();
  return (href) => {
    const abs = /^https?:\/\/[^/]+(\/admin\/[^\s]*)$/i.exec(href || '');
    const path = abs ? abs[1] : href;
    if (!/^\/admin\/analytics\/reports(\?|$)/.test(path || '') || urls.length === 0) return path;
    let idx = urls.findIndex((u, i) => u === path && !used.has(i));
    if (idx === -1) idx = urls.findIndex((_, i) => !used.has(i));
    if (idx === -1) idx = urls.length - 1;
    used.add(idx);
    return urls[idx];
  };
}

// Format any leaf value for display. Critically, never lets an object fall
// through to String() (which yields "[object Object]") — nested objects render
// as a compact "Label: val · Label: val" summary, numbers get thousands
// separators, and booleans read as Yes/No.
const formatValue = (v) => {
  if (v == null) return '–';
  if (typeof v === 'number') return Number.isFinite(v) ? v.toLocaleString() : '–';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.map(formatValue).join(', ');
  if (isPlainObject(v)) {
    return Object.entries(v)
      .filter(([, val]) => val != null)
      .map(([k, val]) => `${prettifyKey(k)}: ${formatValue(val)}`)
      .join(' · ');
  }
  return String(v);
};

// ── CSV export ────────────────────────────────────────────────────
// Flatten a tool result into a multi-section CSV so power users can pull the
// numbers into a spreadsheet, mirroring the Reports-page export.
const csvEscape = (v) => {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function toolResultToCsv (result) {
  if (!isPlainObject(result)) return '';
  const lines = [];
  const scalarRows = [];

  const walk = (obj, prefix) => {
    Object.entries(obj).forEach(([key, value]) => {
      if (value == null) return;
      if (['filter', 'timeRange', 'notes', 'reportsUrl', 'crossTab', 'sections'].includes(key)) return;
      const label = prefix ? `${prefix} › ${prettifyKey(key)}` : prettifyKey(key);

      if (Array.isArray(value)) {
        if (value.length === 0) return;
        if (isPlainObject(value[0])) {
          const cols = Array.from(value.reduce((set, row) => {
            Object.keys(row).forEach((c) => set.add(c));
            return set;
          }, new Set()));
          lines.push('');
          lines.push(csvEscape(label));
          lines.push(cols.map((c) => csvEscape(prettifyKey(c))).join(','));
          value.forEach((row) => {
            lines.push(cols.map((c) => csvEscape(row[c])).join(','));
          });
        } else {
          scalarRows.push([label, value.join('; ')]);
        }
        return;
      }

      if (isPlainObject(value)) {
        // Nested object: emit as its own key/value section.
        const entries = Object.entries(value).filter(([, v]) => v != null);
        if (entries.length === 0) return;
        // If every value is itself a scalar, render a compact 2-col section;
        // otherwise recurse so deep breakdowns still export.
        const allScalar = entries.every(([, v]) => !isPlainObject(v) && !Array.isArray(v));
        if (allScalar) {
          lines.push('');
          lines.push(csvEscape(label));
          entries.forEach(([k, v]) => lines.push(`${csvEscape(prettifyKey(k))},${csvEscape(v)}`));
        } else {
          walk(value, label);
        }
        return;
      }

      scalarRows.push([label, value]);
    });
  };

  walk(result, '');

  const out = [];
  if (scalarRows.length > 0) {
    out.push('Metric,Value');
    scalarRows.forEach(([k, v]) => out.push(`${csvEscape(k)},${csvEscape(v)}`));
  }
  out.push(...lines);
  return out.join('\n');
}

function downloadToolCsv (toolCall) {
  const csv = toolResultToCsv(toolCall?.result);
  if (!csv) return;
  const label = (TOOL_LABELS[toolCall.name] || toolCall.name || 'pulse-data')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const stamp = new Date().toISOString().slice(0, 10);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pulse-${label}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Render a tool result as scannable tables instead of raw JSON:
 *  - array of objects → table (capped at 12 rows)
 *  - nested object of counts (byGender, distribution…) → label/value table
 *  - top-level scalars → key figure rows
 */
function ToolDataRenderer ({ result }) {
  if (result == null) return null;
  if (result.error) {
    return <div style={{ color: '#B71C1C', fontSize: 12 }}>Tool error: {result.error}</div>;
  }
  if (!isPlainObject(result)) {
    return <pre>{JSON.stringify(result, null, 2)}</pre>;
  }

  const scalars = [];
  const tables = [];
  const MAX_ROWS = 12;

  Object.entries(result).forEach(([key, value]) => {
    if (key === 'timeRange' || key === 'filter' || key === 'notes') return;
    if (value == null) return;

    // Deep links (e.g. reportsUrl → pre-filtered Custom Reports page).
    if (key === 'reportsUrl' && typeof value === 'string') {
      scalars.push(['Full report', <a key="rl" href={value}>Open in Custom Reports →</a>]);
      return;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return;
      if (isPlainObject(value[0])) {
        // Time series (e.g. dailySeries) — render as a line chart when there
        // are enough points to be meaningful.
        const numericKey = Object.keys(value[0]).find((k) => k !== 'date' && typeof value[0][k] === 'number');
        if ('date' in value[0] && numericKey && value.length > 2) {
          tables.push(
            <div key={key} style={{ margin: '10px 0' }}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#888', marginBottom: 4 }}>
                {prettifyKey(key)}
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={value} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => String(d).slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={40} />
                  <ChartTooltip contentStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey={numericKey} stroke="#4ECDC4" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          );
          return;
        }
        const cols = Object.keys(value[0]);
        tables.push(
          <DataTable key={key}>
            <caption>{prettifyKey(key)}</caption>
            <thead>
              <tr>{cols.map((c) => <th key={c}>{prettifyKey(c)}</th>)}</tr>
            </thead>
            <tbody>
              {value.slice(0, MAX_ROWS).map((row, i) => (
                <tr key={i}>
                  {cols.map((c) => (
                    <td key={c} className={typeof row[c] === 'number' ? 'num' : ''}>
                      {formatValue(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
              {value.length > MAX_ROWS && (
                <tr><td colSpan={cols.length} className="more-note">…and {value.length - MAX_ROWS} more rows</td></tr>
              )}
            </tbody>
          </DataTable>
        );
      } else {
        scalars.push([prettifyKey(key), value.join(', ')]);
      }
      return;
    }

    if (isPlainObject(value)) {
      const entries = Object.entries(value).filter(([, v]) => v != null);
      if (entries.length === 0) return;
      // Map of records (e.g. metrics: { bloodPressure: {tested, average, …} })
      // → render as a table (rows = keys, cols = union of inner fields) instead
      // of stringifying each object to "[object Object]".
      const allRecords = entries.every(([, v]) => isPlainObject(v));
      if (allRecords) {
        const cols = [...new Set(entries.flatMap(([, v]) => Object.keys(v)))];
        tables.push(
          <DataTable key={key}>
            <caption>{prettifyKey(key)}</caption>
            <thead>
              <tr>
                <th>{prettifyKey(key).replace(/s$/, '') || 'Item'}</th>
                {cols.map((c) => <th key={c}>{prettifyKey(c)}</th>)}
              </tr>
            </thead>
            <tbody>
              {entries.map(([rk, rv]) => (
                <tr key={rk}>
                  <td>{prettifyKey(rk)}</td>
                  {cols.map((c) => (
                    <td key={c} className={typeof rv[c] === 'number' ? 'num' : ''}>{formatValue(rv[c])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </DataTable>
        );
        return;
      }
      tables.push(
        <DataTable key={key}>
          <caption>{prettifyKey(key)}</caption>
          <tbody>
            {entries.map(([k, v]) => (
              <tr key={k}>
                <td>{prettifyKey(k)}</td>
                <td className="num">{formatValue(v)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      );
      return;
    }

    scalars.push([prettifyKey(key), formatValue(value)]);
  });

  return (
    <div className="data-body">
      {scalars.length > 0 && (
        <DataTable>
          <tbody>
            {scalars.map(([label, val]) => (
              <tr key={label}>
                <td>{label}</td>
                <td className="num">{val}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {tables}
      {result.notes && (
        <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', marginTop: 4 }}>{result.notes}</div>
      )}
    </div>
  );
}

function groupConversations (conversations) {
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  const groups = { Today: [], 'Previous 7 Days': [], 'Previous 30 Days': [], Older: [] };
  conversations.forEach((c) => {
    const t = new Date(c.lastMessageAt || c.updatedAt || c.createdAt).getTime();
    const ageDays = (now - t) / dayMs;
    if (ageDays < 1) groups.Today.push(c);
    else if (ageDays < 7) groups['Previous 7 Days'].push(c);
    else if (ageDays < 30) groups['Previous 30 Days'].push(c);
    else groups.Older.push(c);
  });
  return groups;
}

export default function Pulse () {
  const { authStore } = useStore();
  const { setPageHeader } = usePageHeader();
  const userInitials = useMemo(() => {
    const me = authStore?.me;
    const source = me?.fullName || [me?.firstName, me?.lastName].filter(Boolean).join(' ') || me?.email || '';
    const parts = String(source).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [authStore?.me]);

  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [error, setError] = useState(null);

  const scrollRef = useRef(null);
  // When a conversation is auto-created during send, we set activeId to the new
  // id AFTER placing optimistic messages. Skip the load effect for that change
  // so it doesn't refetch the (still-empty) conversation and wipe the chat.
  const skipNextLoadRef = useRef(false);

  useEffect(() => {
    setPageHeader({ title: 'Pulse', count: null, countIcon: null });
  }, [setPageHeader]);

  // Initial load: conversation list
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await PulseService.listConversations();
        if (cancelled) return;
        if (list?.success) {
          setConversations(list.conversations || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load Pulse');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load active conversation messages
  useEffect(() => {
    if (!activeId) {
      setActiveConv(null);
      setMessages([]);
      return;
    }
    if (skipNextLoadRef.current) {
      // Conversation was just created locally during send — messages are
      // already on screen; don't refetch and clobber them.
      skipNextLoadRef.current = false;
      return;
    }
    let cancelled = false;
    setLoadingConv(true);
    (async () => {
      try {
        const data = await PulseService.getConversation(activeId);
        if (cancelled) return;
        if (data?.success) {
          setActiveConv(data.conversation);
          setMessages(data.messages || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoadingConv(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const grouped = useMemo(() => groupConversations(conversations), [conversations]);

  const handleNewChat = useCallback(async () => {
    try {
      const res = await PulseService.createConversation({});
      if (res?.success) {
        setConversations((prev) => [res.conversation, ...prev]);
        setActiveId(res.conversation._id);
      }
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const handleRename = useCallback(async (conv, e) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    const next = window.prompt('Rename conversation', conv.title || '');
    if (!next || next.trim() === conv.title) return;
    try {
      const res = await PulseService.renameConversation(conv._id, next.trim());
      if (res?.success) {
        setConversations((prev) => prev.map((c) => (c._id === conv._id ? res.conversation : c)));
        if (activeConv?._id === conv._id) setActiveConv(res.conversation);
      }
    } catch (err) { setError(err.message); }
  }, [activeConv]);

  const handleDelete = useCallback(async (conv, e) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${conv.title || 'this conversation'}"? This cannot be undone.`)) return;
    try {
      const res = await PulseService.deleteConversation(conv._id);
      if (res?.success) {
        setConversations((prev) => prev.filter((c) => c._id !== conv._id));
        if (activeId === conv._id) {
          setActiveId(null);
        }
      }
    } catch (err) { setError(err.message); }
  }, [activeId]);

  const sendMessage = useCallback(async (text) => {
    if (!text || !text.trim() || sending) return;
    setError(null);

    let convId = activeId;
    let convObj = activeConv;

    // Auto-create on first send if no active conversation
    if (!convId) {
      try {
        const res = await PulseService.createConversation({});
        if (!res?.success) throw new Error(res?.message || 'Failed to start chat');
        convObj = res.conversation;
        convId = convObj._id;
        setConversations((prev) => [convObj, ...prev]);
        skipNextLoadRef.current = true;
        setActiveId(convId);
        setActiveConv(convObj);
      } catch (err) {
        setError(err.message);
        return;
      }
    }

    // Optimistic user bubble + streaming assistant placeholder
    const tempUserMsg = {
      _id: `tmp-${Date.now()}`,
      role: 'user',
      content: text.trim(),
      createdAt: new Date().toISOString()
    };
    const tempAssistantId = `tmp-a-${Date.now()}`;
    const tempAssistantMsg = {
      _id: tempAssistantId,
      role: 'assistant',
      content: '',
      streaming: true,
      toolStatus: null,
      createdAt: new Date().toISOString()
    };
    setMessages((prev) => [...prev, tempUserMsg, tempAssistantMsg]);
    setInput('');
    setSending(true);

    try {
      let failed = null;
      await PulseService.postMessageStream(convId, text.trim(), (evt) => {
        if (evt.type === 'token') {
          setMessages((prev) => prev.map((m) => (
            m._id === tempAssistantId
              ? { ...m, content: m.content + evt.content, toolStatus: null }
              : m
          )));
        } else if (evt.type === 'tool') {
          setMessages((prev) => prev.map((m) => (
            m._id === tempAssistantId
              ? { ...m, toolStatus: TOOL_LABELS[evt.name] || evt.name }
              : m
          )));
        } else if (evt.type === 'done') {
          setMessages((prev) => {
            const withoutTemps = prev.filter((m) => m._id !== tempUserMsg._id && m._id !== tempAssistantId);
            return [...withoutTemps, evt.userMessage, evt.assistantMessage];
          });
          setConversations((prev) => {
            const next = prev.filter((c) => c._id !== convId);
            return [evt.conversation, ...next];
          });
          setActiveConv(evt.conversation);
        } else if (evt.type === 'error') {
          failed = evt.message || 'Send failed';
        }
      });
      if (failed) throw new Error(failed);
    } catch (err) {
      setError(err.message);
      setMessages((prev) => prev.filter((m) => m._id !== tempUserMsg._id && m._id !== tempAssistantId));
      setInput(text);
    } finally {
      setSending(false);
    }
  }, [activeId, activeConv, sending]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <PulseRoot>
      <UtilityBar>
        <Dropdown
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          trigger={['click']}
          placement="bottomRight"
          dropdownRender={() => (
            <HistoryPanel>
              {conversations.length === 0 && (
                <div className="empty">No conversations yet. Ask your first question below.</div>
              )}
              {Object.entries(grouped).map(([label, list]) => (
                list.length > 0 && (
                  <div key={label}>
                    <div className="group-label">{label}</div>
                    {list.map((c) => (
                      <HistoryItem
                        key={c._id}
                        $active={c._id === activeId}
                        onClick={() => { setActiveId(c._id); setHistoryOpen(false); }}
                        title={c.title}
                      >
                        <span className="title">{c.title || 'Untitled'}</span>
                        <span className="actions">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={(e) => handleRename(c, e)}
                            title="Rename"
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={(e) => handleDelete(c, e)}
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </span>
                      </HistoryItem>
                    ))}
                  </div>
                )
              ))}
            </HistoryPanel>
          )}
        >
          <UtilityButton type="button">
            <FontAwesomeIcon icon={faClockRotateLeft} /> History
            {conversations.length > 0 && ` (${conversations.length})`}
          </UtilityButton>
        </Dropdown>
        <Dropdown
          open={promptsOpen}
          onOpenChange={setPromptsOpen}
          trigger={['click']}
          placement="bottomRight"
          dropdownRender={() => (
            <PromptMenu>
              <div className="menu-label">Try asking…</div>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="prompt"
                  onClick={() => { setPromptsOpen(false); sendMessage(s); }}
                >
                  {s}
                </button>
              ))}
            </PromptMenu>
          )}
        >
          <UtilityButton type="button">
            <FontAwesomeIcon icon={faLightbulb} /> Ask
          </UtilityButton>
        </Dropdown>
        <UtilityButton type="button" onClick={handleNewChat}>
          <FontAwesomeIcon icon={faPlus} /> New chat
        </UtilityButton>
      </UtilityBar>

      <MessageScroll ref={scrollRef}>
          <MessageContainer>
            {error && (
              <div style={{
                background: '#FFEBEE', color: '#B71C1C',
                padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13
              }}>
                {error}
              </div>
            )}

            {messages.length === 0 && !loadingConv && !sending && (
              <EmptyState>
                <h2>Talk to your data</h2>
                <p>
                  Ask in plain English about patients, screenings, events, or volunteers.
                  Answers come from live aggregate data — no individual patient details
                  are ever shared with the AI.
                </p>
                <div className="examples">
                  {SUGGESTIONS.slice(0, 6).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="example"
                      onClick={() => sendMessage(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </EmptyState>
            )}

            {messages.map((m) => (
              <Bubble key={m._id} $role={m.role}>
                <div className="avatar">
                  {m.role === 'user'
                    ? userInitials
                    : <FontAwesomeIcon icon={faHeart} />}
                </div>
                <div className="body">
                  {m.role === 'assistant' && m.streaming && !m.content ? (
                    <Typing>
                      <span /><span /><span />
                      &nbsp;{m.toolStatus ? `Checking ${m.toolStatus.toLowerCase()}…` : 'Pulse is thinking…'}
                    </Typing>
                  ) : m.role === 'assistant'
                    ? <Markdown resolveHref={makeHrefResolver(m.toolCalls)}>{m.content}</Markdown>
                    : <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>}
                  {Array.isArray(m.toolCalls) && m.toolCalls.length > 0 && (
                    <ToolCallList>
                      {m.toolCalls.map((tc, idx) => (
                        <ToolCallCard key={idx}>
                          <summary>
                            View the data — {TOOL_LABELS[tc.name] || tc.name}
                          </summary>
                          {tc.result && !tc.result.error && (
                            <div className="card-actions">
                              <button
                                type="button"
                                className="csv-btn"
                                onClick={() => downloadToolCsv(tc)}
                              >
                                <FontAwesomeIcon icon={faDownload} /> Export CSV
                              </button>
                            </div>
                          )}
                          <ToolDataRenderer result={tc.result} />
                        </ToolCallCard>
                      ))}
                    </ToolCallList>
                  )}
                </div>
              </Bubble>
            ))}
          </MessageContainer>
        </MessageScroll>

        <Composer>
          <ComposerInner>
            {messages.length > 0 && !sending && (() => {
              // Prefer AI-generated follow-ups from the latest answer;
              // fall back to the static starter suggestions.
              const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant' && !m.streaming);
              const chips = (lastAssistant?.suggestions?.length ? lastAssistant.suggestions : SUGGESTIONS).slice(0, 3);
              return (
                <Suggestions>
                  {chips.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="pill"
                      onClick={() => sendMessage(s)}
                      disabled={sending}
                    >
                      {s}
                    </button>
                  ))}
                </Suggestions>
              );
            })()}
            <div className="row">
              <textarea
                value={input}
                placeholder="Ask Pulse about your data…"
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending}
                rows={1}
              />
              <button
                type="button"
                className="send"
                disabled={sending || !input.trim()}
                onClick={() => sendMessage(input)}
                title="Send"
              >
                <FontAwesomeIcon icon={faPaperPlane} />
              </button>
            </div>
          </ComposerInner>
        </Composer>
    </PulseRoot>
  );
}
